
import { Database } from "@/integrations/supabase/types";
import { Industry, OrganizationSize } from "@/types/assessment";

type InsightRow = Database['public']['Tables']['assessment_insights']['Row'];

export interface Insight {
  category: InsightRow['category'];
  insight: string;
  confidence_score: number;
  metadata: Record<string, any>;
}

export const categoryIcons = {
  industry_trend: 'TrendingUp',
  adoption_pattern: 'Lightbulb',
  regulatory_impact: 'Scale',
  market_shift: 'Zap',
  technology_advancement: 'Cpu'
} as const;

export const categoryLabels = {
  industry_trend: 'Industry Trend',
  adoption_pattern: 'Adoption Pattern',
  regulatory_impact: 'Regulatory Impact',
  market_shift: 'Market Shift',
  technology_advancement: 'Technology Advancement'
} as const;

export const getIndustrySpecificInsight = (industry: Industry, size: OrganizationSize): string => {
  const insights: Record<string, string> = {
    "Education & EdTech": `Based on your industry (${industry}), focus on AI adoption strategies such as intelligent tutoring systems, automated assessment tools, and personalized learning pathways. Consider implementing AI-powered solutions for student engagement tracking, learning analytics, and adaptive curriculum development.`,
    "Healthcare & Pharma": `Based on your industry (${industry}), focus on AI adoption strategies such as diagnostic assistance tools, patient data analytics, and treatment optimization systems. Consider implementing AI for medical imaging analysis, drug discovery acceleration, and personalized medicine solutions.`,
    "Financial Services": `Based on your industry (${industry}), focus on AI adoption strategies such as fraud detection systems, algorithmic trading platforms, and personalized banking solutions. Consider implementing AI for risk assessment, customer service automation, and regulatory compliance monitoring.`,
    "Manufacturing & Industry": `Based on your industry (${industry}), focus on AI adoption strategies such as predictive maintenance systems, quality control automation, and supply chain optimization. Consider implementing AI for production planning, inventory management, and energy efficiency optimization.`,
    "Retail & E-commerce": `Based on your industry (${industry}), focus on AI adoption strategies such as recommendation engines, inventory forecasting, and personalized marketing systems. Consider implementing AI for customer behavior analysis, pricing optimization, and supply chain management.`,
    "Government & Public Sector": `Based on your industry (${industry}), focus on AI adoption strategies such as citizen service automation, resource allocation optimization, and policy impact analysis. Consider implementing AI for public safety monitoring, urban planning, and social service delivery.`,
    "Real Estate": `Based on your industry (${industry}), focus on AI adoption strategies such as property valuation models, predictive maintenance systems, and virtual property tour solutions. Consider implementing AI for market analysis, tenant screening, building management automation, and energy optimization in properties.`,
    "Other": `Based on your industry profile, focus on AI adoption strategies that align with your specific sector needs, such as process automation, decision support systems, and data analytics solutions. Consider implementing AI for operational efficiency, customer experience enhancement, and strategic planning.`
  };

  return insights[industry] || insights["Other"];
};

export const generateFallbackInsights = (assessmentData: {
  organizationDetails: {
    industry: Industry;
    size: OrganizationSize;
  };
  dimensionScores: Record<string, number>;
}): Insight[] => {
  const { industry, size } = assessmentData.organizationDetails;
  const scores = assessmentData.dimensionScores;
  const avgScore = Object.values(scores).reduce((a, b) => a + b, 0) / Object.values(scores).length;
  const lowestScore = Math.min(...Object.entries(scores).map(([_, score]) => score));
  const lowestDimension = Object.entries(scores).find(([_, score]) => score === lowestScore)?.[0];
  
  return [
    {
      category: 'industry_trend',
      insight: getIndustrySpecificInsight(industry, size),
      confidence_score: 0.85,
      metadata: { industry }
    },
    {
      category: 'adoption_pattern',
      insight: `As a ${size} organization, implement a phased AI adoption approach starting with high-impact, low-complexity projects such as process automation, data analytics dashboards, and customer service chatbots. Focus on solutions that can demonstrate quick wins while building internal capabilities.`,
      confidence_score: 0.9,
      metadata: { size }
    },
    {
      category: 'technology_advancement',
      insight: `With ${lowestDimension} showing the lowest score, prioritize investments in this area. Consider implementing robust data governance frameworks, establishing AI development standards, and building scalable infrastructure for AI deployment. Focus on tools and platforms that support both current needs and future growth.`,
      confidence_score: 0.8,
      metadata: { scores }
    },
    {
      category: 'market_shift',
      insight: `With an average maturity score of ${avgScore.toFixed(1)}, align your AI initiatives with emerging market trends such as MLOps automation, responsible AI practices, and cloud-native AI solutions. Focus on building competitive advantages through AI-driven innovation and operational excellence.`,
      confidence_score: 0.85,
      metadata: { avgScore }
    },
    {
      category: 'regulatory_impact',
      insight: `For a ${size} organization in ${industry}, establish comprehensive governance frameworks including data privacy controls, model validation processes, and ethical AI guidelines. Implement regular audits, bias detection systems, and transparent documentation practices to ensure compliance and trust.`,
      confidence_score: 0.87,
      metadata: { size }
    }
  ];
};
